import { styled } from "@mui/system";
import { motion } from "framer-motion";
import React, { useCallback, useContext, useMemo } from "react";
import {
  LAYOUT_BREAKPOINT_WIDTH,
  SIDEBAR_DATASOURCE,
  SideBarDataSourcesProps,
} from "../../../data/constants";
import { useDimension } from "../../../hooks/index";
import { Spacer } from "../../atoms/Spacer";
import { SideBarFooter } from "./SIdeBarFooter";
import { SidebarItem } from "./SideBarItem";
import { SidebarHeader } from "./SidebarHeader";
import { AuthContext } from "../../../context/AuthContext";
import {
  ADMIN_PERMISSIONS,
  SUPER_ADMIN_PERMISSIONS,
  ACCOUNTANT_PERMISSIONS,
  DONOR_PERMISSIONS,
} from "../../../data/roles";
import { UserRolesEnum } from "../../../typings";

const Wrapper = styled("div")`
  min-width: 52px;
  height: 100vh;

  @media (max-width: ${LAYOUT_BREAKPOINT_WIDTH}px) {
    min-width: 0;
  }
`;

const MainContainer = styled("div")`
  display: flex;
  height: 100vh;
`;

const Sections = styled("section")`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
`;

const MotionDiv = styled(motion.div, {
  shouldForwardProp: (props) => props !== "isOpen",
})<{ isOpen: boolean }>`
  background-color: ${({ theme }) => theme.palette.colors["white"]["400"]};
  color: ${({ theme }) => theme.palette.text.secondary};
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow-y: auto;
  overflow-x: hidden;

  ::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;

  :hover {
    ::-webkit-scrollbar {
      display: block;
      width: ${({ isOpen }) => (isOpen ? "6px" : "0")};
    }

    ::-webkit-scrollbar-track {
      background-color: transparent;
    }

    ::-webkit-scrollbar-thumb {
      background-color: #888;
      border-radius: 8px;
    }
    scrollbar-width: thin;
    scrollbar-track-color: transparent;
    scrollbar-thumb-color: #b3b3b3;
  }
`;

type SideBarProps = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const SideBar: React.ComponentType<SideBarProps> = ({ isOpen, setIsOpen }) => {
  const { width } = useDimension();
  const auth = useContext(AuthContext);

  const isAdmin = [...ADMIN_PERMISSIONS, ...SUPER_ADMIN_PERMISSIONS].includes(
    auth?.role as UserRolesEnum
  );
  const isAccountant = [...ACCOUNTANT_PERMISSIONS].includes(
    auth?.role as UserRolesEnum
  );
  const isDonor = [...DONOR_PERMISSIONS].includes(
    auth?.role as UserRolesEnum
  );
  // Exact role check — true ONLY when the user is specifically a Donor
  const isDonorOnly = auth?.role === UserRolesEnum.DONOR;

  const RenderItem = useCallback(
    ({ item }: { item: SideBarDataSourcesProps }) => {

      return <SidebarItem key={item.path} item={item} isOpen={isOpen} />;
    },
    [isOpen]
  );

  const desktopAnimation = useMemo(() => {
    return {
      width: isOpen ? "180px" : "52px",
      padding: isOpen ? "0 8px" : "0 8px",
      transition: {
        duration: 0.2,
        type: "linear",
      },
      maxHeight: "100vh",
    };
  }, [isOpen]);

  const mobileAnimation = useMemo(() => {
    return {
      width: isOpen ? "180px" : "0",
      padding: isOpen ? "0 8px" : "0",
      transition: {
        duration: 0.2,
        type: "linear",
      },
      maxHeight: "100vh",
    };
  }, [isOpen]);

  const animate = useMemo(
    () =>
      width < LAYOUT_BREAKPOINT_WIDTH ? mobileAnimation : desktopAnimation,
    [desktopAnimation, mobileAnimation, width]
  );

  return (
    <Wrapper>
      <MainContainer>
        <MotionDiv animate={animate} isOpen={isOpen}>
          <Sections>
            <Spacer vertical={1.5} />
            <SidebarHeader isOpen={isOpen} />
            <Spacer vertical={3} />
            {SIDEBAR_DATASOURCE.map((item: SideBarDataSourcesProps) => {
              // Users module is admin only
              if (item.path == "/users" && !isAdmin) {
                return null;
              }

              // Home is for Admin and Donor
              if (item.path == "/home" && !isAdmin && !isDonorOnly) {
                return null;
              }

              // Applications module is hidden for Donors
              if (item.path == "/applications" && isDonorOnly) {
                return null;
              }

              // For accountant and admin modules (accounts, transactions)
              if (
                (item.path == "/accounts" || item.path == "/transactions") &&
                !isAdmin && !isAccountant
              ) {
                return null;
              }
              return <RenderItem item={item} key={item.path} />;
            })}
          </Sections>

          <Sections>
            <Spacer vertical={3} />
            <SideBarFooter isOpen={isOpen} onClick={setIsOpen} />
          </Sections>
        </MotionDiv>
      </MainContainer>
    </Wrapper>
  );
};

export { SideBar };
